import React, { useState, useEffect, useCallback } from 'react';
import './styles/global.css';
import TabBar from './components/TabBar';
import HomePage from './pages/HomePage';
import ObjectsPage from './pages/ObjectsPage';
import QuestionPage from './pages/QuestionPage';
import EventsPage from './pages/EventsPage';
import LibraryPage from './pages/LibraryPage';
import PaywallPage from './pages/PaywallPage';
import PartnerPage from './pages/PartnerPage';
import ExpertTopicsPage from './pages/ExpertTopicsPage';
import OtherPage from './pages/OtherPage';
import AiAssistant from './components/AiAssistant';
import Onboarding from './components/Onboarding';
import OnboardingDev from './components/OnboardingDev';
import PostPaymentDev from './components/PostPaymentDev';
import { getEvents, getSubscription, markNotificationsRead, sendReferral, postpayDone } from './api/client';

// 🧪 Предохранитель песочницы: краш внутри PostPaymentDev показывает ТЕКСТ ошибки
// (для отладки на устройстве) вместо чёрного экрана, и даёт выйти в приложение.
class SandboxErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    try { console.error('[postpay] crash:', error, info); } catch (e) { /* noop */ }
  }
  render() {
    if (this.state.error) {
      const err = this.state.error;
      return (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000, background: 'var(--bg-primary)',
          color: 'var(--text-primary)', padding: '48px 20px 24px', overflow: 'auto',
        }}>
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>🧪 Песочница упала — пришлите скрин этого экрана</h2>
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11.5, lineHeight: 1.45,
            color: '#FF6B6B', background: 'var(--bg-card)', padding: 12, borderRadius: 10,
            marginBottom: 16, maxHeight: '55vh', overflow: 'auto',
          }}>{String((err && (err.stack || err.message)) || err)}</pre>
          <button onClick={this.props.onDismiss} style={{
            width: '100%', padding: '14px 0', background: 'var(--accent)', color: '#0D0D0D',
            border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}>
            Продолжить в приложение →
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Parse startapp deep link parameter
function getStartParam() {
  try {
    const initData = window.Telegram?.WebApp?.initDataUnsafe;
    if (initData?.start_param) return initData.start_param;
    // Fallback: check URL params and hash
    const url = new URL(window.location.href);
    const fromSearch = url.searchParams.get('startapp') || url.searchParams.get('tgWebAppStartParam') || '';
    if (fromSearch) return fromSearch;
    // Check hash fragment (Telegram sometimes puts params there)
    const hash = window.location.hash;
    const match = hash.match(/[?&]tgWebAppStartParam=([^&]*)/);
    if (match) return decodeURIComponent(match[1]);
    return '';
  } catch { return ''; }
}

export default function App() {
  const [tab, setTab] = useState('home');
  const [eventCount, setEventCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [subStatus, setSubStatus] = useState(null); // null = loading, true = active, false = blocked
  const [notification, setNotification] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return !localStorage.getItem('fixhub_onboarding_done'); } catch { return false; }
  });
  const [role, setRole] = useState('member');
  const [onbV2, setOnbV2] = useState(false);
  const [onbPreview, setOnbPreview] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  // 🧪 Песочница: dev-копия воронки (OnboardingDev), список tg_id — в админке «Приложение»
  const [onbDev, setOnbDev] = useState(false);
  const [devDismissed, setDevDismissed] = useState(false);
  const [postPayDismissed, setPostPayDismissed] = useState(false);
  // Боевой триггер послеоплатных слайдов (флаг postpay_live в админке; до этапа G выключен).
  const [postPayPending, setPostPayPending] = useState(false);
  const [postPayLive, setPostPayLive] = useState(false);
  // Доступ открыт вручную из админки (postpay_pending=2): видео-инструкция без «Оплата прошла»
  // и без флага postpay_live. postPayJustPaid — оплата свежая, success-слайд уместен.
  const [postPayGrant, setPostPayGrant] = useState(false);
  const [postPayJustPaid, setPostPayJustPaid] = useState(false);
  // Анкета карточки fix-hub.de: флаг «не заполнена» с бэкенда + ручное открытие
  // (баннер на главной или деп-линк startapp=card_form).
  const [cardFormPending, setCardFormPending] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  // Данные покупки для success-слайда (пакет/период/дата след. списания) — из /api/subscription.
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  // replay: «Ознакомиться с проектом с начала» с Paywall — повторно показать воронку с offer
  const [replayOnboarding, setReplayOnboarding] = useState(false);
  // 🎬 Демо-показ воронки для клиента: t.me/<bot>?startapp=demo, только role=admin.
  // Воронка поверх аппки независимо от доступа; ничего не пишет в БД; промокод TEST → в аппку.
  const [demoDismissed, setDemoDismissed] = useState(false);
  const [startParam] = useState(() => {
    const p = getStartParam();
    console.log('[DeepLink] startParam:', p);
    return p;
  });

  // Pull-to-refresh state
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = React.useRef(0);
  const isPulling = React.useRef(false);
  const PULL_THRESHOLD = 80;

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      // ВАЖНО: методы Bot API существуют в SDK всегда, но БРОСАЮТ WebAppMethodUnsupported,
      // если версия клиента ниже требуемой (проверка `if (tg.method)` НЕ спасает).
      // Непойманный throw здесь = чёрный экран всего приложения на старых Telegram.
      try { tg.setHeaderColor('#0D0D0D'); } catch (e) { /* <6.1 */ }
      try { tg.setBackgroundColor('#0D0D0D'); } catch (e) { /* <6.1 */ }
      // Fullscreen mode (Bot API 8.0+)
      try { if (tg.requestFullscreen) tg.requestFullscreen(); } catch (e) { /* <8.0 */ }
      // Disable swipe-to-close so we can do pull-to-refresh
      try { if (tg.disableVerticalSwipes) tg.disableVerticalSwipes(); } catch (e) { /* <7.7 */ }

      // Apply Telegram safe area insets as CSS variables
      const applySafeArea = () => {
        const sa = tg.safeAreaInset || {};
        const csa = tg.contentSafeAreaInset || {};
        const topInset = (sa.top || 0) + (csa.top || 0);
        const bottomInset = (sa.bottom || 0) + (csa.bottom || 0);
        document.documentElement.style.setProperty('--tg-safe-top', topInset + 'px');
        document.documentElement.style.setProperty('--tg-safe-bottom', bottomInset + 'px');
      };

      // Apply immediately and on events
      applySafeArea();
      tg.onEvent?.('safeAreaChanged', applySafeArea);
      tg.onEvent?.('contentSafeAreaChanged', applySafeArea);
      tg.onEvent?.('fullscreenChanged', applySafeArea);
      // Re-apply after a short delay (fullscreen may take time)
      setTimeout(applySafeArea, 300);
      setTimeout(applySafeArea, 1000);
    }
    // Deep links
    if (startParam && startParam.startsWith('q')) {
      setTab('question');
    } else if (startParam === 'objects') {
      setTab('objects');
    } else if (startParam === 'events') {
      setTab('events');
    } else if (startParam === 'expert_topics') {
      setTab('expert-topics');
    }
  }, [startParam]);

  // Ref-attribution: fire-and-forget при старте с deep-link ref_<digits>
  useEffect(() => {
    if (startParam && startParam.startsWith('ref_')) {
      const inviter = startParam.slice(4);
      if (/^\d+$/.test(inviter) && !localStorage.getItem('fixhub_ref_sent')) {
        sendReferral(inviter);
        try { localStorage.setItem('fixhub_ref_sent', '1'); } catch (e) {}
      }
    }
  }, [startParam]);

  // Деп-линк из DM/письма: t.me/<bot>?startapp=card_form → сразу открываем анкету
  // карточки (только тем, у кого она реально не заполнена — флаг с бэкенда).
  useEffect(() => {
    if (startParam === 'card_form' && cardFormPending) setShowCardForm(true);
  }, [startParam, cardFormPending]);

  // Check subscription
  const checkSubscription = useCallback(() => {
    getSubscription()
      .then(data => {
        setSubStatus(data.active);
        setRole(data.role || 'member');
        setOnbV2(!!data.onb_v2);
        setOnbPreview(!!data.onb_preview);
        setOnbDev(!!data.onb_dev);
        setPostPayPending(!!data.postpay_pending);
        setPostPayLive(!!data.postpay_live);
        setPostPayGrant(!!data.postpay_grant);
        setPostPayJustPaid(!!data.postpay_just_paid);
        setCardFormPending(!!data.card_form_pending);
        if (data.plan || data.next_billing) {
          setPurchaseInfo({ plan: data.plan || '', plan_period: data.plan_period || '', next_billing: data.next_billing || '', expires: data.expires || '' });
        }
        // Show notification if any
        if (data.notifications && data.notifications.length > 0) {
          setNotification(data.notifications[0].text);
          markNotificationsRead().catch(() => {});
        }
      })
      .catch(() => setSubStatus(prev => prev === null ? false : prev));
  }, []);

  // Check on mount
  useEffect(() => { checkSubscription(); }, [checkSubscription]);

  // Re-check subscription every 30 seconds
  useEffect(() => {
    const timer = setInterval(checkSubscription, 30000);
    return () => clearInterval(timer);
  }, [checkSubscription]);

  // Instant re-check when app regains focus (e.g. returning from the Stripe payment tab)
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') checkSubscription(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkSubscription);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkSubscription);
    };
  }, [checkSubscription]);

  // Auto-hide notification after 10 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadEventCount = useCallback(() => {
    getEvents('status=upcoming').then(data => {
      setEventCount(data.counts?.total || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (subStatus) loadEventCount(); }, [loadEventCount, subStatus]);

  // Refresh event count every 30 seconds
  useEffect(() => {
    if (!subStatus) return;
    const timer = setInterval(loadEventCount, 30000);
    return () => clearInterval(timer);
  }, [loadEventCount, subStatus]);

  // Скролл-контейнер: window на мобильном, #root в десктопной колонке (см. global.css)
  const atScrollTop = () => {
    const r = document.getElementById('root');
    return window.scrollY === 0 && (!r || r.scrollTop === 0);
  };

  // Pull-to-refresh handlers
  const handleTouchStart = useCallback((e) => {
    if (atScrollTop()) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0 && atScrollTop()) {
      setPulling(true);
      setPullDistance(Math.min(dy * 0.5, 120));
    } else {
      setPulling(false);
      setPullDistance(0);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD) {
      // Trigger refresh
      setRefreshKey(k => k + 1);
      loadEventCount();
      checkSubscription();
    }
    isPulling.current = false;
    setPulling(false);
    setPullDistance(0);
  }, [pullDistance, loadEventCount, checkSubscription]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setRefreshKey(k => k + 1);
    loadEventCount();
  };

  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    setReplayOnboarding(false);
    try { localStorage.setItem('fixhub_onboarding_done', '1'); } catch {}
    // Also save to Telegram CloudStorage as backup
    try { window.Telegram?.WebApp?.CloudStorage?.setItem('fixhub_onboarding_done', '1'); } catch {}
  }, []);

  // Loading
  if (subStatus === null) {
    return (
      <div style={{
        minHeight: '100dvh', background: 'var(--bg-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 15 }}>Загрузка...</div>
      </div>
    );
  }

  // 🎬 Демо-режим воронки (deep-link startapp=demo, только админ) — раньше всех гейтов.
  if (startParam === 'demo' && role === 'admin' && !demoDismissed) {
    return (
      <SandboxErrorBoundary onDismiss={() => setDemoDismissed(true)}>
        <Onboarding onComplete={() => setDemoDismissed(true)} onbV2={true} demo={true} />
      </SandboxErrorBoundary>
    );
  }

  // Послеоплатные слайды. Показ только при активном триггере postpay_pending:
  //  • боевой — active && postpay_pending при флаге postpay_live (этап G);
  //  • промо (plan_period='promo') — БЕЗ флага postpay_live: акция KAIZENBAUCLUB живая;
  //  • ручная выдача из админки (postpay_grant) — БЕЗ флага: сразу видео-инструкция.
  const promoAccess = purchaseInfo?.plan_period === 'promo';
  const showPostPay = postPayPending && (postPayLive || promoAccess || postPayGrant) && subStatus;
  if (showPostPay && !postPayDismissed) {
    const finishPostPay = () => {
      postpayDone().catch(() => {});   // снимаем триггер (в т.ч. в dev), чтобы дойти до аппки
      setPostPayDismissed(true);
      setDevDismissed(true);           // не возвращаем funnel после прохождения
    };
    return (
      <SandboxErrorBoundary onDismiss={() => setPostPayDismissed(true)}>
        <PostPaymentDev onComplete={finishPostPay} justPaid={!postPayGrant && (promoAccess || postPayJustPaid)} purchase={purchaseInfo} plan={purchaseInfo?.plan || ''} grant={postPayGrant} />
      </SandboxErrorBoundary>
    );
  }

  // Анкета карточки fix-hub.de по запросу: баннер на главной или деп-линк card_form.
  // Не гейт — открывается вручную, крестик возвращает в аппку без снятия триггера.
  // ВАЖНО: postpayDone тут НЕ зовём — «Позже»/закрытие не должны гасить баннер.
  // Баннер гаснет сам, когда анкета реально отправлена (card_applications на бэке).
  if (showCardForm) {
    const finishCardForm = () => {
      setShowCardForm(false);
      checkSubscription();             // отправил анкету → флаг с бэка снимет баннер
    };
    // Пустой план (тест-показ у админа) не должен прятать слайд анкеты → фолбэк standard.
    const cardPurchase = { ...(purchaseInfo || {}), plan: purchaseInfo?.plan || 'standard' };
    return (
      <SandboxErrorBoundary onDismiss={() => setShowCardForm(false)}>
        <PostPaymentDev onComplete={finishCardForm} justPaid={false} purchase={cardPurchase} plan={cardPurchase.plan} forceRegistration={true} />
        <button
          onClick={() => setShowCardForm(false)}
          aria-label="Закрыть"
          style={{
            position: 'fixed', top: 'calc(var(--tg-safe-top, 0px) + 14px)', right: 14, zIndex: 10001,
            width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border)',
            background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
            fontSize: 17, lineHeight: '30px', textAlign: 'center', padding: 0, cursor: 'pointer',
          }}>✕</button>
      </SandboxErrorBoundary>
    );
  }

  // 🧪 Песочница: dev-копия воронки поверх приложения (только tg_id из админ-настройки).
  // Закрыл крестиком / дошёл до конца → обычное приложение до следующего открытия.
  if (onbDev && !devDismissed) {
    return <OnboardingDev onComplete={() => setDevDismissed(true)} onbV2={true} preview={true} />;
  }

  // Force-preview: конкретные tg_id из ONBOARDING_V2_IDS видят v2-онбординг при каждом открытии
  if (onbPreview && !previewDismissed) {
    return <Onboarding onComplete={() => setPreviewDismissed(true)} onbV2={true} preview={true} />;
  }

  // Onboarding (only for unpaid users)
  if (!subStatus && showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} onbV2={onbV2} preview={onbPreview} replay={replayOnboarding} />;
  }

  // Paywall
  if (!subStatus) {
    return <PaywallPage onReplayOnboarding={() => { setReplayOnboarding(true); setShowOnboarding(true); }} />;
  }

  const renderPage = () => {
    switch (tab) {
      case 'home': return <HomePage key={refreshKey} onNavigate={handleTabChange} role={role} cardFormPending={cardFormPending} onOpenCardForm={() => setShowCardForm(true)} />;
      case 'objects': return <ObjectsPage onNavigate={handleTabChange} />;
      case 'question': return <QuestionPage key={refreshKey} startParam={startParam} />;
      case 'events': return <EventsPage key={refreshKey} onNavigate={handleTabChange} />;
      case 'other': return <OtherPage onNavigate={handleTabChange} role={role} />;
      case 'library': return <LibraryPage key={refreshKey} />;
      case 'partner': return <PartnerPage onNavigate={handleTabChange} />;
      case 'expert-topics': return <ExpertTopicsPage onNavigate={handleTabChange} />;
      default: return <HomePage key={refreshKey} onNavigate={handleTabChange} role={role} cardFormPending={cardFormPending} onOpenCardForm={() => setShowCardForm(true)} />;
    }
  };

  return (
    <div
      style={{ minHeight: '100dvh', background: 'var(--bg-primary)' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {pulling && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: pullDistance, overflow: 'hidden',
          transition: pullDistance === 0 ? 'height 0.2s ease' : 'none',
        }}>
          <div style={{
            width: 28, height: 28,
            border: '3px solid var(--bg-elevated)',
            borderTopColor: pullDistance >= PULL_THRESHOLD ? 'var(--accent)' : 'var(--text-tertiary)',
            borderRadius: '50%',
            transform: `rotate(${pullDistance * 3}deg)`,
            transition: 'border-color 0.2s',
            animation: pullDistance >= PULL_THRESHOLD ? 'none' : 'none',
          }} />
        </div>
      )}
      {/* In-app notification banner */}
      {notification && (
        <div onClick={() => setNotification(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          padding: '14px 20px', paddingTop: 'calc(var(--tg-safe-top, 0px) + 14px)',
          background: 'linear-gradient(135deg, #FFCC00 0%, #FFB800 100%)',
          color: '#0D0D0D', fontSize: 14, fontWeight: 600, lineHeight: 1.4,
          textAlign: 'center', cursor: 'pointer',
          animation: 'slideDown 0.3s ease',
          boxShadow: '0 4px 20px rgba(255,204,0,0.3)',
        }}>
          {notification}
        </div>
      )}
      {renderPage()}

      {/* AI Assistant floating button — only on home tab */}
      {tab === 'home' && !showAssistant && (
        <div onClick={() => setShowAssistant(true)} style={{
          position: 'fixed',
          bottom: 'calc(86px + var(--tg-safe-bottom, 0px))',
          right: 16, zIndex: 99,
          width: 52, height: 52, borderRadius: 16,
          background: 'linear-gradient(135deg, var(--accent), #E6B800)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(255,204,0,0.35)',
          animation: 'fadeInUp 0.3s ease 0.5s both',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            <circle cx="12" cy="10" r="1" fill="#0A0A0A" stroke="none"/>
            <circle cx="8" cy="10" r="1" fill="#0A0A0A" stroke="none"/>
            <circle cx="16" cy="10" r="1" fill="#0A0A0A" stroke="none"/>
          </svg>
        </div>
      )}

      <AiAssistant visible={showAssistant} onClose={() => setShowAssistant(false)} />

      <TabBar active={tab} onChange={handleTabChange} eventCount={eventCount} />
      <style>{`
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
